import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, AlertCircle } from 'lucide-react';
import SurveyAssessment from './SurveyAssessment';
import { getAssessmentById } from './AssessmentData';

const AssessmentPage = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAssessment = () => {
      try {
        setLoading(true);
        const assessmentData = getAssessmentById(assessmentId);
        
        if (!assessmentData) {
          throw new Error('Assessment not found');
        }
        
        setAssessment(assessmentData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [assessmentId]);

  const handleSubmit = (answers) => {
    // In a real implementation, this would make an API call to save the answers
    if (assessment) {
      assessment.questions.forEach(question => {
        question.status = 'complete';
      });
    }
    navigate('/participant-dashboard');
  };

  const handleCancel = () => {
    navigate('/participant-dashboard');
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex flex-col items-center justify-center p-4"
      >
        <div className="text-center">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mb-4" />
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="min-h-screen flex flex-col items-center justify-center p-4"
      >
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6 mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
            Error Loading Assessment
          </h1>
          <p className="text-gray-600 text-center mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/participant-dashboard')}
            className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="assessment"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen py-8"
      >
        {assessment && (
          <SurveyAssessment
            assessment={assessment}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default AssessmentPage;